/**
 * Central registry of RTK Query APIs used for tag-based cache operations.
 *
 * ⚠️  IMPORTANT: When creating a new RTK Query API, you MUST register it here.
 *     Otherwise getActiveTags() won't report its tags to the AI agent context,
 *     and invalidateTagsByName() won't be able to invalidate its cache via WebSocket events.
 *     See CLAUDE.md § "RTK Query cache rules" for details.
 */
import { partnerCampaigns } from '../type-partner/campaigns/campaigns.api';
import { partnerEvent } from '../type-partner/events/partner-event.api';
import { partnerCreatives } from '../type-partner/creatives/creatives.api';
import { partnerCreativesDeskApi } from '../type-partner/creatives-desk/creativesDesk.api';
import { partnerMentions } from '../type-partner/mentions/mentions.api';
import { partnerLandings } from '../type-partner/landings/landings.api';
import { adPartnerCredentials } from '../type-partner/credentials/credentials.api';
import { partnerEventCatalog } from '../type-partner/events-catalog/partner-event-catalog.api';
import { partnerSnippetsApi } from '../type-partner/snippets/snippets.api';
import { partnerBriefsApi } from '../type-partner/briefs/briefs.api';
import { partnerTopicsApi } from '../type-partner/topics/topics.api';
import { partnerLeadMagnetsApi } from '../type-partner/lead-magnets/lead-magnets.api';
import { partnerLeadQualificationsApi } from '../type-partner/lead-qualifications/lead-qualifications.api';
import { partnerMeetingsApi } from '../type-partner/meetings/meetings.api';
import { automationApi } from '../type-partner/automation/automation.api';
import { analytics } from '../analytics/analytics.api';
import { landingPagesApi } from '../landing-pages/landing-pages.api';
import { organizationInvitesApi } from '../main/organization-invites/organizationInvites.api';
import { attendeeEventsApi } from '../type-attendee/events/attendee-events.api';
import { attendeeFavoritesApi } from '../type-attendee/favorites/attendee-favorites.api';
import { attendeeMatchApi } from '../type-attendee/match/match.api';
import { followReportsApi } from '../main/follow-reports/follow-reports.api';
import { organizerLandings } from '../type-organizer/landings/landings.api';
import { onboardingApi } from '../onboarding/onboarding.api';
import { presentationApi } from '../presentation/presentation.api';
import { mailingUnsubscribeApi } from '../mailing-unsubscribe/mailingUnsubscribe.api';
import { productAnalyticsApi } from '../main/product-analytics/productAnalytics.api';
import { adminApi } from '../type-admin/admin.base';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface ApiEntry {
  reducerPath: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  api: { util: { invalidateTags: (tags: any) => any } };
  tagTypes: string[];
}

/**
 * All RTK Query APIs that participate in AI-agent cache invalidation.
 * Each entry maps a reducerPath to the API instance and its tag types.
 */
export const API_REGISTRY: ApiEntry[] = [
  { reducerPath: 'partner_campaigns', api: partnerCampaigns, tagTypes: ['partner_campaigns'] },
  { reducerPath: 'partner_event', api: partnerEvent, tagTypes: ['partner_event'] },
  { reducerPath: 'partner_creatives', api: partnerCreatives, tagTypes: ['partner_creatives'] },
  {
    reducerPath: 'partner_creatives_desk',
    api: partnerCreativesDeskApi,
    tagTypes: ['partner_creatives_desk'],
  },
  { reducerPath: 'partner_mentions', api: partnerMentions, tagTypes: ['partner_mentions'] },
  { reducerPath: 'partner_landings', api: partnerLandings, tagTypes: ['partner_landings'] },
  {
    reducerPath: 'partner_credentials',
    api: adPartnerCredentials,
    tagTypes: ['partner_credentials'],
  },
  {
    reducerPath: 'partner_event_catalog',
    api: partnerEventCatalog,
    tagTypes: ['partner_event_catalog'],
  },
  { reducerPath: 'partner_snippets', api: partnerSnippetsApi, tagTypes: ['partner_snippets'] },
  { reducerPath: 'partner_briefs', api: partnerBriefsApi, tagTypes: ['partner_briefs'] },
  { reducerPath: 'partner_topics', api: partnerTopicsApi, tagTypes: ['partner_topics'] },
  {
    reducerPath: 'partner_lead_magnets',
    api: partnerLeadMagnetsApi,
    tagTypes: ['partner_lead_magnets'],
  },
  {
    reducerPath: 'partner_lead_qualifications',
    api: partnerLeadQualificationsApi,
    tagTypes: ['partner_lead_qualifications', 'partner_lead_qualifications_rules'],
  },
  { reducerPath: 'partner_meetings', api: partnerMeetingsApi, tagTypes: ['partner_meetings'] },
  {
    reducerPath: 'automation',
    api: automationApi,
    tagTypes: ['automation_flows', 'automation_attendees', 'automation_attendee_info'],
  },
  { reducerPath: 'partner_analytics', api: analytics, tagTypes: ['partner_analytics'] },
  { reducerPath: 'landings_pages', api: landingPagesApi, tagTypes: ['landings_pages'] },
  {
    reducerPath: 'organization_invites',
    api: organizationInvitesApi,
    tagTypes: ['organization_invites'],
  },
  // ATTENDEE
  { reducerPath: 'attendee_events', api: attendeeEventsApi, tagTypes: ['attendee_events'] },
  {
    reducerPath: 'attendee_favorites',
    api: attendeeFavoritesApi,
    tagTypes: ['attendee_favorites'],
  },
  { reducerPath: 'attendee_match', api: attendeeMatchApi, tagTypes: ['attendee_match'] },
  {
    reducerPath: 'follow_reports',
    api: followReportsApi,
    tagTypes: ['follow_reports', 'follow_report_schedules'],
  },
  // ORGANIZER
  {
    reducerPath: 'organizer_landings',
    api: organizerLandings,
    tagTypes: ['organizer_landings'],
  },
  // ONBOARDING
  {
    reducerPath: 'onboarding',
    api: onboardingApi,
    tagTypes: ['onboarding'],
  },
  // PRESENTATION (public start page)
  {
    reducerPath: 'presentation',
    api: presentationApi,
    tagTypes: ['presentation'],
  },
  // MAILING UNSUBSCRIBE (public footer link of a mailing)
  {
    reducerPath: 'mailing_unsubscribe',
    api: mailingUnsubscribeApi,
    tagTypes: ['mailing_unsubscribe'],
  },
  // PRODUCT ANALYTICS (telemetry boot config)
  {
    reducerPath: 'product_analytics',
    api: productAnalyticsApi,
    tagTypes: ['product_analytics'],
  },
  // ADMIN
  {
    reducerPath: 'admin_api',
    api: adminApi,
    tagTypes: [
      'admin_agent_threads',
      'admin_artifacts',
      'admin_ai_files',
      'admin_matching',
      'admin_premade_creatives',
      'admin_premade_company_assets',
      'admin_creative_ideas',
      'admin_creative_idea_categories',
      'admin_event_creative_ideas',
      'admin_filter_options',
    ],
  },
];

/** Reducer paths for scanning active subscriptions */
export const API_REDUCER_PATHS = API_REGISTRY.map((entry) => entry.reducerPath);

/** Maps tag type → API dispatcher for cache invalidation */
export const TAG_DISPATCHERS: Record<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { api: { util: { invalidateTags: (tags: any) => any } }; tagType: string }
> = {};

for (const entry of API_REGISTRY) {
  for (const tagType of entry.tagTypes) {
    TAG_DISPATCHERS[tagType] = { api: entry.api, tagType };
  }
}
